import { useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/brand.config";
import { useApi } from "@/pages/admin/control_centre/_kit";
import { MODULE_NAV } from "./moduleNav";
import FundleBrainFAB from "@/pages/admin/_fundle_brain_fab";

export default function ModuleLayout() {
  const { module } = useParams();
  const { data: m } = useApi(`/modules/${module}`, [module]);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState({});
  const Icon = Icons[m?.icon] || Icons.Box;
  const sections = MODULE_NAV[module] || [];
  const base = `/m/${module}`;
  return (
    <div className="min-h-screen flex" style={{ background: "var(--workspace-bg)" }} data-testid={`module-shell-${module}`}>
      <aside className="admin-sidebar w-64 m-3 flex flex-col shrink-0 sticky top-3 max-h-[calc(100vh-1.5rem)]" data-testid="module-sidebar">
        <div className="p-5 border-b border-white/10">
          <a href="/suite" className="text-[10px] uppercase tracking-[0.3em] text-[#B08D57] hover:text-white" data-testid="module-back">← Module Suite</a>
          <div className="flex items-center gap-2 mt-3"><Icon size={18} className="text-[#B08D57] shrink-0" /><div className="font-display text-xl tracking-tight text-white leading-tight" data-testid="module-title">{m?.name || "…"}</div></div>
          <div className="text-[10px] text-white/45 mt-1">{m?.tagline}</div>
          <div className="mt-3 flex items-center gap-2"><img src={BRAND.logoWhiteUrl} alt={BRAND.name} className="h-3.5 w-auto opacity-90" /><span className="text-[9px] uppercase tracking-[0.18em] text-white/35">· Powered by</span><img src={BRAND.platformLogoUrl} alt={BRAND.platform} className="h-3 w-auto opacity-90" /></div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {sections.map((s) => {
            const collapsed = open[s.label] === false;
            return (
              <div key={s.label} className="mb-1">
                <button onClick={() => setOpen({ ...open, [s.label]: collapsed })} className="w-full flex items-center justify-between px-5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-white/35 hover:text-white/60" data-testid={`module-section-${s.label}`}>
                  <span>{s.label}</span><Icons.ChevronRight className={`w-3 h-3 transition-transform ${collapsed ? "" : "rotate-90"}`} />
                </button>
                {!collapsed && s.items.map(([p, label, end]) => p.startsWith("/")
                  ? <a key={p} href={p} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-5 py-2 text-[13px] text-white/75 hover:bg-white/5" data-testid={`module-nav-${p.replace(/\W+/g, "-")}`}>{label}</a>
                  : <NavLink key={p} to={`${base}${p ? `/${p}` : ""}`} end={!!end} data-testid={`module-nav-${p || "dashboard"}`}
                    className={({ isActive }) => `flex items-center gap-3 px-5 py-2 text-[13px] hover:bg-white/5 ${isActive ? "active" : "text-white/75"}`}>{label}</NavLink>)}
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/70 mb-1 truncate" data-testid="module-user">{user?.name || user?.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">{user?.role?.replace(/_/g, " ")}</div>
          <button onClick={async () => { await logout(); nav("/enterprise/login"); }} className="text-xs text-white/70 hover:text-white flex items-center gap-2" data-testid="module-logout"><Icons.LogOut className="w-3.5 h-3.5" /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden min-w-0">{m && <Outlet context={m} />}</main>
      <FundleBrainFAB />
    </div>
  );
}
